import React, { useEffect, useRef, useState } from "react";
import { X, Loader2, Camera, ImageIcon } from "lucide-react";
import api, { apiError } from "@/lib/api";

// Compress client-side to a JPEG Blob (keeps uploads small & fast).
async function compressToBlob(file, maxDim = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = Math.min(maxDim / width, maxDim / height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("compress failed"))),
          "image/jpeg",
          quality
        );
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const READY_MIME = new Set(["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp", "image/gif"]);

function isHeicLike(file) {
  const name = (file?.name || "").toLowerCase();
  const type = (file?.type || "").toLowerCase();
  return type.includes("heic") || type.includes("heif") || /\.heic$|\.heif$/.test(name);
}

/** Append as File when possible; fall back to Blob+filename (older WebViews). */
function appendImagePart(form, blobOrFile, filename, mime) {
  try {
    form.append("files", new File([blobOrFile], filename, { type: mime }));
  } catch (_) {
    const blob =
      blobOrFile instanceof Blob
        ? blobOrFile
        : new Blob([blobOrFile], { type: mime });
    form.append("files", blob, filename);
  }
}

/** Phones/tablets: native capture input. Desktop: getUserMedia modal. */
function prefersNativeCameraCapture() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return true;
  const ua = navigator.userAgent || "";
  if (/Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return true;
  try {
    if (window.matchMedia("(pointer: coarse)").matches) return true;
    if (window.matchMedia("(hover: none)").matches) return true;
  } catch (_) {
    /* ignore */
  }
  return false;
}

function canUseWebcam() {
  return typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia;
}

// Do NOT use Tailwind `hidden` (display:none) — some mobile browsers ignore
// programmatic .click() on display:none file inputs.
const FILE_INPUT_STYLE = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
  opacity: 0,
};

export default function PhotoPicker({ photos, onChange, max = 20, testId = "photo-picker" }) {
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamError, setWebcamError] = useState("");
  const [webcamStarting, setWebcamStarting] = useState(false);

  const stopWebcam = () => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const closeWebcam = () => {
    stopWebcam();
    setWebcamOpen(false);
    setWebcamError("");
    setWebcamStarting(false);
  };

  useEffect(() => () => stopWebcam(), []);

  useEffect(() => {
    if (!webcamOpen) return undefined;
    let cancelled = false;
    setWebcamStarting(true);
    setWebcamError("");

    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
          } catch (_) {
            /* autoplay policies — user can still see frame once playing */
          }
        }
      } catch (err) {
        if (!cancelled) {
          setWebcamError(
            err?.name === "NotAllowedError"
              ? "Povolte přístup ke kameře v prohlížeči, nebo použijte „Z galerie“."
              : "Webkameru se nepodařilo spustit. Na PC použijte „Z galerie“, nebo povolte kameru."
          );
        }
      } finally {
        if (!cancelled) setWebcamStarting(false);
      }
    })();

    return () => {
      cancelled = true;
      stopWebcam();
    };
  }, [webcamOpen]);

  const openGallery = (e) => {
    // PhotoPicker must not sit inside a <label> that also owns the file input —
    // stop label/form side-effects from eating the gesture.
    e.preventDefault();
    e.stopPropagation();
    if (busy) return;
    galleryRef.current?.click();
  };

  const openCamera = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || photos.length >= max) return;
    setError("");

    // Mobile / tablet: native camera via capture attribute (iOS/Android).
    if (prefersNativeCameraCapture()) {
      cameraRef.current?.click();
      return;
    }

    // Desktop: capture is usually ignored → webcam modal when possible.
    if (canUseWebcam()) {
      setWebcamOpen(true);
      return;
    }

    setError("Na tomto zařízení webkamera není dostupná. Použijte „Z galerie“.");
  };

  const uploadFiles = async (files, inputEl) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    setError("");
    try {
      const room = Math.max(0, max - photos.length);
      const chosen = list.slice(0, room);
      const form = new FormData();
      let count = 0;
      let sawHeic = false;
      for (const f of chosen) {
        if (isHeicLike(f)) sawHeic = true;
        try {
          const blob = await compressToBlob(f);
          appendImagePart(form, blob, "photo.jpg", "image/jpeg");
          count += 1;
          continue;
        } catch (_) {
          // Fall back to the original file when canvas compress fails (common for
          // some mobile formats). Backend resolves MIME via extension/magic bytes.
          const mime = (f.type || "").split(";")[0].trim().toLowerCase();
          const name = f.name || "photo.jpg";
          if (READY_MIME.has(mime) || /\.(jpe?g|png|webp|gif)$/i.test(name)) {
            const safeName = /\.(jpe?g|png|webp|gif)$/i.test(name) ? name : "photo.jpg";
            appendImagePart(form, f, safeName, READY_MIME.has(mime) ? mime : "image/jpeg");
            count += 1;
          }
        }
      }
      if (count === 0) {
        setError(
          sawHeic
            ? "Formát HEIC/HEIF nejde nahrát. V telefonu zvolte JPG, nebo fotku nejdřív převeďte."
            : "Fotku se nepodařilo připravit. Zkuste prosím jiný obrázek (JPG/PNG)."
        );
        return;
      }
      // Let the browser set multipart boundary (do not force Content-Type).
      const { data } = await api.post("/uploads", form);
      const urls = data?.urls || [];
      if (!urls.length) {
        setError("Server nevrátil URL fotky. Zkuste to prosím znovu.");
        return;
      }
      onChange([...photos, ...urls]);
    } catch (err) {
      setError(apiError(err) || "Nahrání fotek se nezdařilo. Zkuste to prosím znovu.");
    } finally {
      setBusy(false);
      if (inputEl) inputEl.value = ""; // allow re-selecting the same file
    }
  };

  const onPick = async (e) => {
    await uploadFiles(e.target.files, e.target);
  };

  const captureFromWebcam = async () => {
    const video = videoRef.current;
    if (!video || busy) return;
    const w = video.videoWidth || 0;
    const h = video.videoHeight || 0;
    if (!w || !h) {
      setWebcamError("Kamera ještě není připravená. Počkejte chvíli a zkuste znovu.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    canvas.getContext("2d").drawImage(video, 0, 0, w, h);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
    if (!blob) {
      setWebcamError("Snímek se nepodařilo pořídit.");
      return;
    }
    let file;
    try {
      file = new File([blob], `webcam-${Date.now()}.jpg`, { type: "image/jpeg" });
    } catch (_) {
      file = blob;
      file.name = `webcam-${Date.now()}.jpg`;
    }
    closeWebcam();
    await uploadFiles([file]);
  };

  const removeAt = (idx) => onChange(photos.filter((_, i) => i !== idx));
  const full = photos.length >= max;

  return (
    <div data-testid={testId} className="relative">
      {/* Galerie: bez capture — výběr ze souborů / galerie */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        style={FILE_INPUT_STYLE}
        tabIndex={-1}
        aria-hidden="true"
        onChange={onPick}
        data-testid={`${testId}-input`}
      />
      {/* Vyfotit (mobil): capture spustí fotoaparát. Bez multiple + čisté accept. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={FILE_INPUT_STYLE}
        tabIndex={-1}
        aria-hidden="true"
        onChange={onPick}
        data-testid={`${testId}-camera-input`}
      />

      {photos.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 mb-3">
          {photos.map((p, i) => (
            <div key={i} className="relative group">
              <img src={p} alt="" className="stb-photo-thumb" loading="lazy" />
              <button
                type="button"
                onClick={() => removeAt(i)}
                className="absolute top-1 right-1 bg-[#0E0E0E] text-white p-1 border border-[#FF6B00] hover:bg-[#FF6B00] hover:text-[#0E0E0E]"
                data-testid={`${testId}-remove-${i}`}
                aria-label="Smazat foto"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={openGallery}
          disabled={busy || full}
          className="stb-btn stb-btn-secondary disabled:opacity-50"
          data-testid={`${testId}-add`}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ImageIcon size={16} />}
          Z galerie
        </button>
        <button
          type="button"
          onClick={openCamera}
          disabled={busy || full}
          className="stb-btn stb-btn-secondary disabled:opacity-50"
          data-testid={`${testId}-camera`}
        >
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
          Vyfotit
        </button>
      </div>

      {error && (
        <p className="text-[#FF6B00] text-sm mt-2" data-testid={`${testId}-error`}>
          {error}
        </p>
      )}
      <p className="stb-label mt-2">{busy ? "Nahrávám fotky…" : `${photos.length}/${max} fotek`}</p>

      {webcamOpen && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Webkamera"
          data-testid={`${testId}-webcam-modal`}
          onClick={(ev) => {
            if (ev.target === ev.currentTarget) closeWebcam();
          }}
        >
          <div className="w-full max-w-lg bg-[#0E0E0E] border-2 border-[#FF6B00] p-4 text-white shadow-xl">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h3 className="stb-label text-[#FF6B00]">Vyfotit webkamerou</h3>
              <button
                type="button"
                onClick={closeWebcam}
                className="p-1 border border-[#262626] hover:border-[#FF6B00]"
                aria-label="Zavřít"
                data-testid={`${testId}-webcam-close`}
              >
                <X size={16} />
              </button>
            </div>

            <div className="relative bg-black border border-[#262626] aspect-video overflow-hidden">
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                className="h-full w-full object-cover"
                data-testid={`${testId}-webcam-video`}
              />
              {webcamStarting && !webcamError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm text-[#A3A3A3]">
                  Spouštím kameru…
                </div>
              )}
            </div>

            {webcamError ? (
              <p className="text-[#FF6B00] text-sm mt-3" data-testid={`${testId}-webcam-error`}>
                {webcamError}
              </p>
            ) : (
              <p className="text-xs text-[#A3A3A3] mt-3">
                Na mobilu se otevře fotoaparát; na PC tato webkamera.
              </p>
            )}

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={captureFromWebcam}
                disabled={busy || webcamStarting || !!webcamError}
                className="stb-btn stb-btn-primary disabled:opacity-50"
                data-testid={`${testId}-webcam-shot`}
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                Pořídit
              </button>
              <button
                type="button"
                onClick={closeWebcam}
                className="stb-btn stb-btn-secondary"
                data-testid={`${testId}-webcam-cancel`}
              >
                Zrušit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
